import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { getDemoConfig, sendDemoMessage } from '../api/demo'
import type { DemoConfig, DemoAgent } from '../api/demo'
import { getDemoSessionKey, getDemoMessagesUsed, setDemoMessagesUsed } from '../utils/demo'

interface DemoMessage {
  role: 'system' | 'user' | 'agent'
  content: string
  agent_name?: string
  agent_emoji?: string
  agent_color?: string
}

const EXAMPLE_PROMPTS = [
  'Write a blog post about…',
  'Draft a cold email to…',
  'Audit my website\'s SEO',
]

export default function DemoChat() {
  const navigate = useNavigate()
  const [config, setConfig] = useState<DemoConfig | null>(null)
  const [messages, setMessages] = useState<DemoMessage[]>([
    {
      role: 'system',
      content: '👋 Try Aspired AI free — no sign up needed. Ask me anything or pick an agent below.',
      agent_name: 'Aspired AI',
      agent_emoji: '⚡',
    },
  ])
  const [input, setInput] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null)
  const [messagesUsed, setMessagesUsed] = useState(0)
  const [isLimitReached, setIsLimitReached] = useState(false)
  // Resolved after mount, not in a useState initializer: this component now
  // renders inside the pre-rendered industry landing pages, and the initializer
  // would run during renderToStaticMarkup where localStorage does not exist.
  const [sessionKey, setSessionKey] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    setSessionKey(getDemoSessionKey())
    getDemoConfig()
      .then((cfg) => {
        setConfig(cfg)
        const used = getDemoMessagesUsed()
        setMessagesUsed(used)
        if (used >= cfg.messages_allowed) setIsLimitReached(true)
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    // Only scroll when there are messages — not on initial mount (empty array)
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages, isTyping])

  function getActiveAgent(): DemoAgent | null {
    if (!config) return null
    if (selectedAgent) return config.agents.find((a) => a.id === selectedAgent) ?? null
    return null
  }

  async function handleSend() {
    if (!input.trim() || isTyping || isLimitReached) return
    const text = input.trim()
    setInput('')
    setMessages((prev) => [...prev, { role: 'user', content: text }])
    setIsTyping(true)

    try {
      // sessionKey is empty until the mount effect runs; fall back so a very
      // fast first send still carries a stable key.
      const res = await sendDemoMessage(text, selectedAgent, sessionKey || getDemoSessionKey())
      setMessages((prev) => [
        ...prev,
        {
          role: 'agent',
          content: res.response,
          agent_name: res.agent_name,
          agent_emoji: res.agent_emoji,
          agent_color: res.agent_color,
        },
      ])
      setDemoMessagesUsed(res.messages_used)
      setMessagesUsed(res.messages_used)
      if (res.messages_remaining === 0) setIsLimitReached(true)
    } catch (err: any) {
      if (err.response?.data?.error === 'demo_limit_reached') {
        setIsLimitReached(true)
      } else {
        setMessages((prev) => [
          ...prev,
          {
            role: 'agent',
            content: 'Something went wrong. Please try again.',
            agent_name: 'Aspired AI',
            agent_emoji: '⚡',
            agent_color: '#7C3AED',
          },
        ])
      }
    } finally {
      setIsTyping(false)
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const activeAgent = getActiveAgent()
  const sendColor = activeAgent?.color ?? '#7C3AED'
  const showPrompts = messages.length === 1

  return (
    <div className="demo-chat-card">

      {/* ── TOP BAR ── */}
      <div className="demo-chat-topbar">
        <div className="demo-live-badge">⚡ Live Demo</div>

        {/* Agent selector */}
        {config && (
          <div className="demo-agent-pills">
            <button
              className={`demo-agent-pill${!selectedAgent ? ' active' : ''}`}
              style={!selectedAgent ? { background: '#7C3AED', color: '#fff' } : {}}
              onClick={() => setSelectedAgent(null)}
            >
              Auto
            </button>
            {config.agents.map((agent) => (
              <button
                key={agent.id}
                className={`demo-agent-pill${selectedAgent === agent.id ? ' active' : ''}`}
                style={
                  selectedAgent === agent.id
                    ? { background: agent.color, color: '#fff' }
                    : {}
                }
                onClick={() => setSelectedAgent(agent.id)}
              >
                {agent.emoji} {agent.name}
              </button>
            ))}
          </div>
        )}

        {/* Counter */}
        {config && (
          <div className="demo-counter">
            <span className="demo-counter-text">
              {messagesUsed} / {config.messages_allowed}
            </span>
            <div className="demo-counter-dots">
              {Array.from({ length: config.messages_allowed }).map((_, i) => (
                <span
                  key={i}
                  className={`demo-dot${i < messagesUsed ? ' used' : ''}`}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── MESSAGES ── */}
      <div className="demo-messages-area">
        {showPrompts && (
          <div className="demo-prompt-chips">
            {EXAMPLE_PROMPTS.map((p) => (
              <button
                key={p}
                className="demo-prompt-chip"
                onClick={() => {
                  setInput(p)
                  textareaRef.current?.focus()
                }}
              >
                {p}
              </button>
            ))}
          </div>
        )}

        {messages.map((msg, i) => {
          if (msg.role === 'system') {
            return (
              <div key={i} className="demo-msg-system">
                <span className="demo-msg-system-emoji">{msg.agent_emoji}</span>
                <span>{msg.content}</span>
              </div>
            )
          }
          if (msg.role === 'user') {
            return (
              <div key={i} className="demo-msg-user">
                <div className="demo-bubble-user">{msg.content}</div>
              </div>
            )
          }
          return (
            <div key={i} className="demo-msg-agent">
              <div
                className="demo-agent-label"
                style={{ color: msg.agent_color }}
              >
                {msg.agent_emoji} {msg.agent_name}
              </div>
              <div
                className="demo-bubble-agent"
                style={{ borderLeftColor: msg.agent_color }}
              >
                {msg.content}
              </div>
            </div>
          )
        })}

        {isTyping && (
          <div className="demo-msg-agent">
            <div className="demo-typing-indicator" style={{ color: sendColor }}>
              <span /><span /><span />
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* ── INPUT / CTA ── */}
      {isLimitReached ? (
        <div className="demo-limit-cta">
          <div className="demo-limit-headline">🎉 You've seen what your AI team can do</div>
          <div className="demo-limit-sub">
            Sign up free to unlock all your messages and all agents
          </div>
          <div className="demo-limit-btns">
            <button
              className="btn-primary btn-lg"
              onClick={() => navigate('/register')}
            >
              Start Free — It's Quick
            </button>
            <button
              className="btn-outline"
              onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
            >
              See Pricing
            </button>
          </div>
          <div className="demo-limit-fine">No credit card required</div>
        </div>
      ) : (
        <div className="demo-input-area">
          <textarea
            ref={textareaRef}
            className="demo-textarea"
            placeholder="Ask anything — try 'write me a blog post' or 'draft a cold email'…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={2}
            disabled={isTyping}
          />
          <button
            className="demo-send-btn"
            style={{ background: sendColor }}
            onClick={handleSend}
            disabled={!input.trim() || isTyping}
            aria-label="Send message"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>
      )}

    </div>
  )
}
